// material-ui
import { makeStyles } from '@material-ui/core/styles';
import TextField, { BaseTextFieldProps } from '@material-ui/core/TextField';
import React, { useEffect, useState } from 'react';

import { publicaColors } from '~/src/dash/utils/colors';

// COMPONENT DESCRIPTION: Focused textfield should be our new go to for
// textfield component.  The new functionality we use for this field
// is it will highlight and change colors while it is focused by the user.

interface IProps extends BaseTextFieldProps {
  onChange: (value: any) => void;
  // these will enable validations for the textfield
  enableAutoFocus?: boolean;
  enableNumbersOnly?: boolean;
  enableXMLProof?: boolean;
  enableIntegersOnly?: boolean;
  enablePositiveValuesOnly?: boolean;
  enableAlignRight?: boolean;
  maxValue?: number;
  minValue?: number;
  // default helper text value which can be overwritten by validations
  defaultHelperText?: string;
  enableHelperText?: boolean;
  error?: boolean;
  startAdornment?: any;
  endAdornment?: any;
}

const useStyles = makeStyles({
  input: {
    color: '#212529',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: '#9c9c9c',
      fontSize: 12,
    },
    border: '0.3px solid #B5B5B5',
    borderRadius: 3,
  },
  inputFocused: {
    color: '#212529',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: '#9c9c9c',
      fontSize: 12,
    },
    border: '0.3px solid #4C9AFF',
    boxShadow: '0 0 2px rgba(76, 154, 255, 0.5)',
    borderRadius: 3,
  },
  inputDisabled: {
    color: 'grey',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: 'grey',
      fontSize: 12,
    },
    border: '0.3px solid #B5B5B5',
    borderRadius: 3,
  },
  inputError: {
    color: '#ff0000',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    border: `0.3px solid ${publicaColors.red}`,
    borderRadius: 3,
  },
  inputRight: {
    color: '#212529',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: '#9c9c9c',
      fontSize: 12,
    },
    border: '0.3px solid #B5B5B5',
    borderRadius: 3,
    textAlign: 'right',
  },
  inputFocusedRight: {
    color: '#212529',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: '#9c9c9c',
      fontSize: 12,
    },
    border: '0.3px solid #4C9AFF',
    boxShadow: '0 0 2px rgba(76, 154, 255, 0.5)',
    borderRadius: 3,
    textAlign: 'right',
  },
  inputDisabledRight: {
    color: 'lightgrey',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    '&::placeholder': {
      color: '#9c9c9c',
      fontSize: 12,
    },
    border: '0.3px solid #B5B5B5',
    borderRadius: 3,
    textAlign: 'right',
  },
  inputErrorRight: {
    color: '#ff0000',
    padding: '2px 4px 0px 4px',
    fontSize: 14,
    border: '0.3px solid #B5B5B5',
    borderRadius: 3,
    textAlign: 'right',
  },
});

export const FocusedTextfield = (props: IProps) => {
  const [focused, setFocused] = useState(false);
  const {
    enableAutoFocus = false,
    defaultHelperText = '',
    enableHelperText,
    startAdornment,
    endAdornment,
    ...otherProps
  } = props;

  const {
    enableAlignRight,
    error,
    disabled,
    onChange,
    enableXMLProof,
    enableIntegersOnly,
    enablePositiveValuesOnly,
    enableNumbersOnly,
    maxValue,
    minValue,
  } = otherProps;

  const classes = useStyles();
  const [helperText, setHelperText] = useState(defaultHelperText);
  const [hasError, setHasError] = useState(error);

  // validates values, returns if any helpertext errors without changing
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // if the value is empty
    if (event.target.value === '') {
      setHelperText(defaultHelperText);
      setHasError(false);
      onChange(event);
      return;
    }
    const helperTexts = [] as string[];
    const value = Number(event.target.value);
    const strValue = event.target.value;
    if (enableNumbersOnly && Number.isNaN(value)) {
      helperTexts.push('Value must be a number.');
    }
    if (maxValue !== undefined) {
      if (value !== undefined && !Number.isNaN(value) && value > maxValue) {
        helperTexts.push('Value greater than maximum value.');
      }
    }
    if (minValue !== undefined) {
      if (value !== undefined && !Number.isNaN(value) && value < minValue) {
        helperTexts.push('Value less than minimum value.');
      }
    }
    if (enableIntegersOnly) {
      if (value !== undefined && !Number.isInteger(value)) {
        helperTexts.push('Only integer values allowed.');
      }
    }
    if (enablePositiveValuesOnly && !Number.isNaN(value) && value < 0) {
      helperTexts.push('Only positive values allowed.');
    }
    if (enableXMLProof) {
      for (let i = 0; i < strValue.length; i++) {
        if (['"', "'", '>', '<', '&'].includes(strValue[i])) {
          helperTexts.push('Values ", \', >, <, and & not allowed.');
          break;
        }
      }
    }
    if (helperTexts.length > 0) {
      setHelperText(helperTexts.join(' '));
      setHasError(true);
      setTimeout(() => {
        setHelperText(defaultHelperText);
        setHasError(false);
      }, 1800);
    } else {
      setHelperText(defaultHelperText);
      setHasError(false);
      onChange(event);
    }
  };

  const inputDisabled = enableAlignRight ? classes.inputDisabledRight : classes.inputDisabled;
  const inputError = enableAlignRight ? classes.inputErrorRight : classes.inputError;
  const inputFocused = enableAlignRight ? classes.inputFocusedRight : classes.inputFocused;
  const input = enableAlignRight ? classes.inputRight : classes.input;

  useEffect(() => {
    setHasError(error);
  }, [error]);

  useEffect(() => {
    setHelperText(defaultHelperText);
  }, [defaultHelperText]);

  return (
    // @ts-ignore
    <TextField
      {...otherProps}
      disabled={!!disabled}
      autoFocus={enableAutoFocus}
      type={props.type === 'number' ? 'number' : ''}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={handleChange}
      helperText={enableHelperText ? helperText : ''}
      error={error}
      InputProps={{
        disableUnderline: true,
        classes: {
          input: disabled ? inputDisabled : hasError ? inputError : focused ? inputFocused : input,
        },
        startAdornment,
        endAdornment,
      }}
    />
  );
};
